import { z } from 'zod';
import { IsoTime, ServerId } from './basics.js';
/**
 * Where an address is, as far as Bartizo knows (P11.11).
 *
 * The only source is CrowdSec: an alert carries the country it resolved the
 * attacker to, which the agent keeps in the event's `raw`. There is no GeoIP
 * database of Bartizo's own, so an address CrowdSec never raised an alert about has
 * no country, and the map says how much it could not place instead of guessing.
 */
/** ISO 3166-1 alpha-2, upper case. */
export const CountryCode = z.string().regex(/^[A-Z]{2}$/);
/** The country in an event's `raw` (`crowdsec.source.country`), or null. Read back from storage, so parsed. */
export function countryOf(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const source = raw['crowdsec'];
    if (typeof source !== 'object' || source === null)
        return null;
    const inner = source['source'];
    if (typeof inner !== 'object' || inner === null)
        return null;
    const country = inner['country'];
    if (typeof country !== 'string')
        return null;
    const parsed = CountryCode.safeParse(country.toUpperCase());
    return parsed.success ? parsed.data : null;
}
/** Where an address is and whose network it is: from DB-IP, approximate. */
export const GeoInfo = z.object({
    country: CountryCode.nullable(),
    asn: z.number().int().nonnegative().nullable(),
    /** The network's owner, as DB-IP names it. */
    network: z.string().max(200).nullable(),
});
export const THREAT_MAP_HOURS_MAX = 24 * 30;
/** `GET /v1/threat-map`. */
export const ThreatMapQuery = z
    .object({
    hours: z.coerce.number().int().min(1).max(THREAT_MAP_HOURS_MAX).default(24),
    server_id: ServerId.optional(),
})
    .strict();
export const ThreatMapCountry = z.object({
    code: CountryCode,
    events: z.number().int().nonnegative(),
    /** Different source addresses. */
    addresses: z.number().int().nonnegative(),
    /** Of the events, how many were high or critical. */
    high: z.number().int().nonnegative(),
    lastSeenAt: IsoTime,
});
export const ThreatMapResponse = z.object({
    from: IsoTime,
    to: IsoTime,
    /** Busiest first. */
    countries: z.array(ThreatMapCountry).max(250),
    /** Events with an address whose country is not known. */
    unplaced: z.object({
        events: z.number().int().nonnegative(),
        addresses: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=country.js.map